/**
 * Preload Screen Component
 * Shows loading progress while preloading all data at startup
 */

import React from 'react';

const PreloadScreen = ({ progress, message, stats, error, onRetry }) => {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, #0176d3 0%, #032d60 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Salesforce Sans, Arial, sans-serif',
      color: 'white',
      zIndex: 99999
    }}>
      {/* Logo */}
      <div style={{
        marginBottom: '40px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '48px',
          fontWeight: 'bold',
          marginBottom: '8px',
          textShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          Afikim Water
        </div>
        <div style={{
          fontSize: '16px',
          opacity: 0.8
        }}>
          Enterprise Water Management System
        </div>
      </div>

      {error ? (
        /* Error State */
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px'
          }}>
            ⚠️
          </div>
          <div style={{
            fontSize: '18px',
            fontWeight: 'bold',
            marginBottom: '12px'
          }}>
            Connection Error
          </div>
          <div style={{
            fontSize: '14px',
            opacity: 0.8,
            marginBottom: '24px'
          }}>
            {error}
          </div>
          <button
            onClick={onRetry}
            style={{
              background: 'white',
              color: '#0176d3',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 32px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'transform 0.2s'
            }}
            onMouseOver={e => e.target.style.transform = 'scale(1.05)'}
            onMouseOut={e => e.target.style.transform = 'scale(1)'}
          >
            Try Again
          </button>
        </div>
      ) : (
        /* Loading State */
        <>
          {/* Progress Ring */}
          <div style={{
            position: 'relative',
            width: '120px',
            height: '120px',
            marginBottom: '32px'
          }}>
            <svg
              width="120"
              height="120"
              style={{ transform: 'rotate(-90deg)' }}
            >
              {/* Background circle */}
              <circle
                cx="60"
                cy="60"
                r="54"
                fill="none"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="8"
              />
              {/* Progress circle */}
              <circle
                cx="60"
                cy="60"
                r="54"
                fill="none"
                stroke="white"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 54}`}
                strokeDashoffset={`${2 * Math.PI * 54 * (1 - progress / 100)}`}
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
            </svg>
            {/* Percentage */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '28px',
              fontWeight: 'bold'
            }}>
              {progress}%
            </div>
          </div>

          {/* Status Message */}
          <div style={{
            fontSize: '18px',
            marginBottom: '24px',
            minHeight: '24px'
          }}>
            {message || 'Loading...'}
          </div>

          {/* Stats (when available) */}
          {stats && (
            <div style={{
              display: 'flex',
              gap: '32px',
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '16px 32px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {stats.objectsWithData || 0}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Objects</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {(stats.totalRecords || 0).toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Records</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {stats.duration ? `${(stats.duration / 1000).toFixed(1)}s` : '-'}
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>Load Time</div>
              </div>
            </div>
          )}

          {/* Animated dots */}
          <div style={{
            marginTop: '32px',
            display: 'flex',
            gap: '8px'
          }}>
            {[0, 1, 2].map(i => (
              <div
                key={i}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: 'white',
                  opacity: 0.6,
                  animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`
                }}
              />
            ))}
          </div>

          <style>{`
            @keyframes pulse {
              0%, 80%, 100% { transform: scale(1); opacity: 0.6; }
              40% { transform: scale(1.3); opacity: 1; }
            }
          `}</style>
        </>
      )}

      {/* Version footer */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        fontSize: '12px',
        opacity: 0.6
      }}>
        v2.0.0 | Powered by Afikim
      </div>
    </div>
  );
};

export default PreloadScreen;
